// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/mission-launch/OperationalReadiness.jsx

import React, { useMemo, useState } from 'react';
import MissionCard from './shared/MissionCard';
import ReadinessScore from './shared/ReadinessScore';

const categoryGuidance = {
  organization: {
    detail: 'Organization context supports launch personalization and executive reporting.',
    value: 'Launch context',
    ready: 'Organization identity, business context, countries, critical systems, audit timing, and launch objectives can be reviewed.',
    attention: 'Confirm that ownership, business scope, countries, and audit timing match leadership expectations.',
    action: 'Review the saved organization profile before relying on launch assumptions.',
    readyShort: 'Context available',
    attentionShort: 'Confirm assumptions',
    actionShort: 'Review profile',
  },
  frameworks: {
    detail: 'Selected frameworks define the modeled launch scope and control-mapping context.',
    value: 'Launch scope',
    ready: 'Selected frameworks are available for initial mapping and guided review.',
    attention: 'Validate framework scope against customer commitments, audit timing, and internal priorities.',
    action: 'Confirm the selected launch frameworks before adding optional scope.',
    readyShort: 'Scope selected',
    attentionShort: 'Validate scope',
    actionShort: 'Confirm framework plan',
  },
  connectors: {
    detail: 'Connected providers can supply read-only source-system signals for Mission Launch.',
    value: 'Provider state',
    ready: 'Authoritative Enterprise connector state is available for the selected providers.',
    attention: 'Assign ownership for connector health, evidence freshness, and reauthorization.',
    action: 'Review provider health before relying on connected evidence.',
    readyShort: 'State available',
    attentionShort: 'Assign owner',
    actionShort: 'Review health',
  },
  evidence: {
    detail: 'Evidence readiness reflects modeled launch availability, not a verified evidence inventory.',
    value: 'Launch estimate',
    ready: 'Mission Launch has enough context to present an initial evidence-readiness model.',
    attention: 'Review persisted evidence, freshness, coverage, and confidence before validation decisions.',
    action: 'Open Evidence and Validation before closing work or reporting progress.',
    readyShort: 'Model available',
    attentionShort: 'Check freshness',
    actionShort: 'Review evidence',
  },
  identity: {
    detail: 'Identity readiness indicates whether identity-related capabilities can be reviewed.',
    value: 'Launch estimate',
    ready: 'Identity-related connector and review paths may be available.',
    attention: 'Review MFA, privileged access, dormant accounts, service accounts, and administrator exposure.',
    action: 'Use measured identity findings and evidence to prioritize work.',
    readyShort: 'Review path ready',
    attentionShort: 'Review admin access',
    actionShort: 'Inspect identity data',
  },
  workflow: {
    detail: 'Workflow readiness indicates whether guided operator workflows are available.',
    value: 'Capability model',
    ready: 'Guided execution, validation, and operator-review paths are available.',
    attention: 'Define remediation approvers, evidence validators, and closure authority.',
    action: 'Keep remediation approvals and closure decisions explicit.',
    readyShort: 'Workflow available',
    attentionShort: 'Set approvers',
    actionShort: 'Confirm controls',
  },
  validation: {
    detail: 'Validation readiness indicates whether validation paths are available for operator use.',
    value: 'Capability model',
    ready: 'Validation paths are available for evidence-backed review.',
    attention: 'Confirm evidence freshness, connector health, and validation ownership before closure.',
    action: 'Use measured validation results before closing findings.',
    readyShort: 'Paths available',
    attentionShort: 'Confirm owner',
    actionShort: 'Validate before close',
  },
  learning: {
    detail: 'Learning readiness reflects advisory capability availability under human control.',
    value: 'Advisory capability',
    ready: 'Advisory memory, confidence context, and similar-outcome recall may be available.',
    attention: 'Review explanations before relying on learning guidance.',
    action: 'Treat learning output as decision support, not autonomous authority.',
    readyShort: 'Advisory available',
    attentionShort: 'Review rationale',
    actionShort: 'Use as support',
  },
  ai_readiness: {
    detail: 'AI readiness reflects advisory guidance availability under Mission Launch guardrails.',
    value: 'Advisory capability',
    ready: 'OASYS can explain priorities and support operator decisions.',
    attention: 'Define approved AI use and preserve human approval for execution and closure.',
    action: 'Use AI guidance as decision support only.',
    readyShort: 'Guidance available',
    attentionShort: 'Define AI use',
    actionShort: 'Keep human approval',
  },
};

const fallbackCategories = Object.entries(categoryGuidance).map(([key, guidance]) => ({
  key,
  label: key === 'ai_readiness'
    ? 'AI Readiness'
    : key.charAt(0).toUpperCase() + key.slice(1),
  score: null,
  status: 'Unavailable',
  classification: 'unavailable',
  source: null,
  ...guidance,
}));

function normalizeCategories(categories) {
  if (!Array.isArray(categories) || !categories.length) {
    return fallbackCategories;
  }

  return categories.map((category, index) => {
    const key =
      category?.key ||
      String(category?.label || index).toLowerCase().replace(/\s+/g, '_');
    const guidance = categoryGuidance[key] || {};

    return {
      ...guidance,
      ...category,
      key,
      classification: category?.classification || 'modeled',
      source: category?.source || 'mission_launch_readiness_model.v1',
    };
  });
}

function scoreTone(score) {
  const numeric = Number(score);
  if (!Number.isFinite(numeric)) return 'review';
  if (numeric >= 92) return 'strong';
  if (numeric >= 86) return 'ready';
  return 'review';
}

function detailEyebrow(category = {}) {
  if (!category.label) return 'Selected Category Review';
  return `${category.label} Review`;
}

function formatStatusLabel(value) {
  const normalized = String(value || '').trim();
  if (!normalized) return '—';

  return normalized
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function categoryStatusLabel(category = {}) {
  const key = String(category?.key || '').trim().toLowerCase();
  const status = String(category?.status || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ');

  if (key === 'connectors' && status === 'needs connection') {
    return 'Additional coverage available';
  }

  return formatStatusLabel(category?.status);
}

function truthLabel(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'measured') return 'Measured';
  if (normalized === 'derived') return 'Derived';
  if (normalized === 'modeled') return 'Modeled launch estimate';
  if (normalized === 'unavailable') return 'Unavailable';
  return 'Source not classified';
}

function displayScore(score) {
  const numeric = Number(score);
  return Number.isFinite(numeric) ? `${numeric}%` : '—';
}

function compactDetailItems(category = {}) {
  return [
    { label: 'Ready', value: category.readyShort || category.status || '—' },
    { label: 'Watch', value: category.attentionShort || 'Review source data' },
    { label: 'Next', value: category.actionShort || 'Review details' },
  ];
}

export default function OperationalReadiness({
  readiness = {},
  loading = false,
  error = '',
  saving = false,
  onRetry,
  onContinue,
}) {
  const categories = useMemo(
    () => normalizeCategories(readiness.categories),
    [readiness.categories]
  );

  const [selectedCategoryKey, setSelectedCategoryKey] = useState(
    categories[0]?.key || 'organization'
  );
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  const selectedCategory =
    categories.find((category) => category.key === selectedCategoryKey) ||
    categories[0];

  const scoreTruth = readiness.score_truth || {};
  const readinessScore =
    scoreTruth.available === false
      ? null
      : Number(scoreTruth.value ?? readiness.score ?? readiness.readiness_score);

  const interpretation = readiness.interpretation || {
    title: 'Launch readiness is a modeled onboarding assessment.',
    detail:
      'Review Mission Control and Mission Execution for measured posture, active findings, persisted evidence, and current priorities.',
    recommended_action: 'Review measured operational state before taking action.',
  };

  const selectCategory = (categoryKey) => {
    setSelectedCategoryKey(categoryKey);
    setDetailsExpanded(false);
  };

  return (
    <div className="mission-step-shell mission-operational-readiness-page">
      <MissionCard
        eyebrow="Phase 6 · Operational Readiness"
        title="OASYS has modeled launch readiness across the operating system"
        description="Readiness reflects onboarding context and capability availability. It is not a compliance grade, audit result, or measured operational posture."
      >
        {error ? (
          <div className="connector-launch-error" role="alert">
            <strong>Operational readiness unavailable.</strong>
            <span>{error}</span>
            {typeof onRetry === 'function' ? (
              <button type="button" onClick={onRetry}>Retry</button>
            ) : null}
          </div>
        ) : null}

        <ReadinessScore
          score={Number.isFinite(readinessScore) ? readinessScore : 0}
          label="Modeled Launch Readiness"
          detail={
            scoreTruth.explanation ||
            readiness.explanation ||
            'This modeled score estimates readiness to begin guided operations.'
          }
        />

        <section
          className="readiness-action-panel"
          aria-label="Operational readiness interpretation"
        >
          <div>
            <p className="mission-launch-eyebrow">Readiness Interpretation</p>
            <h4>{interpretation.title}</h4>
            <p>{interpretation.detail}</p>
            <p>
              <strong>Recommended next action:</strong>{' '}
              {interpretation.recommended_action}
            </p>
          </div>
          <span className="mission-status-pill">
            {truthLabel(scoreTruth.classification || readiness.classification)}
          </span>
        </section>

        <section
          className="readiness-category-section"
          aria-label="Operational readiness categories"
        >
          <div className="readiness-category-section-header">
            <div>
              <p className="mission-launch-eyebrow">Readiness Categories</p>
              <h4>
                Every category preserves the detailed review while identifying
                the modeled source.
              </h4>
            </div>
            <span className="mission-status-pill">Clickable review</span>
          </div>

          <div className="readiness-category-grid readiness-category-grid-detailed">
            {categories.map((category) => {
              const tone = scoreTone(category.score);
              const selected = category.key === selectedCategory?.key;

              return (
                <button
                  type="button"
                  className={`readiness-category-card readiness-category-card-${tone}${selected ? ' readiness-category-card-selected' : ''}`}
                  key={category.key}
                  onClick={() => selectCategory(category.key)}
                  aria-pressed={selected}
                >
                  <span className="readiness-category-label">
                    {category.label}
                  </span>
                  <strong>{displayScore(category.score)}</strong>
                  <em>{categoryStatusLabel(category)}</em>
                  <div
                    className="readiness-category-bar"
                    style={{
                      '--category-score': Number.isFinite(Number(category.score))
                        ? `${category.score}%`
                        : '0%',
                    }}
                  >
                    <span />
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {selectedCategory ? (
          <section
            className={`readiness-category-detail-panel readiness-category-detail-panel-polished${detailsExpanded ? ' readiness-category-detail-panel-expanded' : ''}`}
            aria-label={`${selectedCategory.label} readiness detail`}
          >
            <div className="readiness-category-detail-copy">
              <p className="mission-launch-eyebrow">
                {detailEyebrow(selectedCategory)}
              </p>
              <h4>{categoryStatusLabel(selectedCategory)}</h4>
              <p>{selectedCategory.detail}</p>

              {!detailsExpanded ? (
                <div
                  className="readiness-category-compact-insights"
                  aria-label="Selected readiness summary"
                >
                  {compactDetailItems(selectedCategory).map((item) => (
                    <div key={item.label}>
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              ) : null}

              {detailsExpanded ? (
                <div className="readiness-category-detail-breakdown">
                  <div>
                    <span>What’s ready</span>
                    <p>{selectedCategory.ready}</p>
                  </div>
                  <div>
                    <span>Needs attention</span>
                    <p>{selectedCategory.attention}</p>
                  </div>
                  <div>
                    <span>Recommended next action</span>
                    <p>{selectedCategory.action}</p>
                  </div>
                  <div>
                    <span>Truth classification</span>
                    <p>
                      {truthLabel(selectedCategory.classification)}
                      {selectedCategory.source
                        ? ` · ${selectedCategory.source}`
                        : ''}
                    </p>
                  </div>
                </div>
              ) : null}

              <button
                type="button"
                className="mission-secondary-button readiness-view-details-button"
                onClick={() => setDetailsExpanded((value) => !value)}
              >
                {detailsExpanded ? 'Hide Details' : 'View Details'}
              </button>
            </div>

            <div className="readiness-category-detail-score">
              <strong>{displayScore(selectedCategory.score)}</strong>
              <span>
                {truthLabel(selectedCategory.classification)}
              </span>
            </div>
          </section>
        ) : null}

        <div className="mission-step-actions mission-readiness-actions">
          <button
            type="button"
            className="mission-primary-button"
            onClick={onContinue}
            disabled={loading || saving || Boolean(error)}
          >
            {saving
              ? 'Confirming Readiness…'
              : 'Continue to Mission Ready'}
          </button>
        </div>
      </MissionCard>
    </div>
  );
}
